import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref as dbRef, get, push } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { useAuth } from "../context/AuthContext";
import { Gift, Plus, X, Camera, AlertCircle, MessageCircle, ArrowLeft } from "lucide-react";

function GiveawayCard({ item }) {
  const waMsg = encodeURIComponent(
    `Hi! I'm interested in "${item.title}" that you're giving away on CampusSwap. Is it still available?`
  );
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
      <div className="h-32 bg-gray-100 overflow-hidden">
        {item.imageUrl
          ? <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Gift size={36} className="text-gray-200" /></div>}
      </div>
      <div className="p-3">
        <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">FREE</span>
        <p className="font-semibold text-gray-800 text-sm mt-1.5 line-clamp-1">{item.title}</p>
        {item.description && <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">{item.description}</p>}
        <p className="text-xs text-gray-400 mt-1">{item.postedByName} · {item.college}</p>
        {item.whatsapp && (
          <a href={`https://wa.me/91${item.whatsapp.replace(/\D/g, "")}?text=${waMsg}`}
            target="_blank" rel="noreferrer"
            className="mt-2 flex items-center justify-center gap-1.5 w-full bg-green-500 text-white py-1.5 rounded-xl text-xs font-semibold hover:bg-green-600">
            <MessageCircle size={13} /> Claim Item
          </a>
        )}
      </div>
    </div>
  );
}

export default function Giveaway() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", condition: "", whatsapp: userProfile?.phone || "" });

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    setLoading(true);
    try {
      const snap = await get(dbRef(db, "giveaways"));
      if (snap.exists()) {
        const all = Object.entries(snap.val()).map(([id, data]) => ({ id, ...data }));
        setItems(all.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        setItems([]);
      }
    } catch (e) {  }
    setLoading(false);
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  function handleImage(e) {
    const f = e.target.files[0];
    if (f) { setImageFile(f); setPreview(URL.createObjectURL(f)); }
  }

  async function handlePost(e) {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || form.title.trim().length < 5) return setError("Item name must be at least 5 characters.");
    if (!imageFile) return setError("Please add a photo of the item you are giving away.");
    if (!form.whatsapp.trim() || !/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, "")))
      return setError("Enter a valid 10-digit WhatsApp number.");
    setPosting(true);
    try {
      let imageUrl = null;
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
      }
      await push(dbRef(db, "giveaways"), {
        title: form.title.trim(),
        description: form.description.trim(),
        condition: form.condition,
        whatsapp: form.whatsapp.trim(),
        imageUrl,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || "Student",
        college: userProfile?.college || "",
        status: "active",
        createdAt: new Date().toISOString(),
      });
      setForm({ title: "", description: "", condition: "", whatsapp: userProfile?.phone || "" });
      setImageFile(null); setPreview(null);
      setShowForm(false);
      fetchItems();
    } catch (err) { setError("Failed to post. Try again."); }
    setPosting(false);
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-r from-teal-500 to-green-500 px-4 pt-4 pb-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="text-xl font-bold">Free Giveaways</h1>
              <p className="text-teal-100 text-xs mt-0.5">Pass on what you no longer need</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-white text-teal-700 px-3 py-1.5 rounded-xl font-semibold text-sm flex items-center gap-1">
            <Plus size={16} /> Give Away
          </button>
        </div>
      </div>

      <div className="px-4 mt-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-3">
            {[1,2,3,4].map(i => <div key={i} className="h-44 bg-white rounded-xl animate-pulse" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Gift size={48} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-medium">No giveaways yet</p>
            <p className="text-gray-400 text-sm mt-1">Be the first to give something away!</p>
            <button onClick={() => setShowForm(true)} className="mt-4 bg-teal-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm">
              Post a Giveaway
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map(item => <GiveawayCard key={item.id} item={item} />)}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end">
          <div className="bg-white rounded-t-2xl w-full">
            <div className="px-4 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-800 text-lg">Give Away an Item</h3>
              <button onClick={() => { setShowForm(false); setError(""); }}><X size={24} /></button>
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3 pb-24">
              {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm"><AlertCircle size={16} />{error}</div>}
              <div onClick={() => document.getElementById("gv-img").click()}
                className="border-2 border-dashed border-teal-300 rounded-xl h-28 flex items-center justify-center cursor-pointer overflow-hidden">
                {preview ? <img src={preview} alt="p" className="h-full w-full object-cover" />
                  : <div className="text-teal-400 text-center"><Camera size={24} className="mx-auto mb-1" /><p className="text-xs">Add photo (optional)</p></div>}
              </div>
              <input id="gv-img" type="file" accept="image/*" onChange={handleImage} className="hidden" />
              <input name="title" value={form.title} onChange={handleChange} required
                placeholder="Item name (e.g. Lab Coat, Drawing Board)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-teal-400" />
              <select name="condition" value={form.condition} onChange={handleChange}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-teal-400 text-gray-700">
                <option value="">Condition (optional)</option>
                {["Like New", "Good", "Fair", "Used"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea name="description" value={form.description} onChange={handleChange} rows={2}
                placeholder="Details, pickup location, any conditions..."
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-teal-400 resize-none" />
              <input name="whatsapp" type="tel" value={form.whatsapp} onChange={handleChange} required
                placeholder="Your WhatsApp number *"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-teal-400" />
              <button type="submit" disabled={posting}
                className="w-full bg-teal-500 text-white py-3.5 rounded-xl font-semibold hover:bg-teal-600 disabled:opacity-50">
                {posting ? "Posting..." : "Post Giveaway"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
